/**
 * INFINITY LIFE 2.0 — Agenda & Task Management Component
 */

import { store } from '../state.js';
import { api, showToast } from '../api.js';
import { getPriorityMeta, getLocationMeta, formatTimeBR } from '../utils/formatters.js';
import { getRelativeDateLabel } from '../utils/date.js';

let activeTab = 'today'; // 'today' | 'upcoming' | 'overdue' | 'completed' | 'all'
let activePriority = 'all';
let activeScope = 'all'; // 'all' | 'work' | 'personal'

export function renderTaskList() {
  const { tasks } = store.get();

  // Filter tasks locally based on active filters
  const filteredTasks = tasks.filter((t) => {
    // Tab filter
    const today = new Date().toISOString().split('T')[0];
    if (activeTab === 'today') {
      if (t.due_date !== today) return false;
    } else if (activeTab === 'upcoming') {
      if (!t.due_date || t.due_date <= today) return false;
    } else if (activeTab === 'overdue') {
      if (!t.due_date || t.due_date >= today || t.status === 'completed' || t.status === 'cancelled') return false;
    } else if (activeTab === 'completed') {
      if (t.status !== 'completed') return false;
    }

    // Priority filter
    if (activePriority !== 'all' && t.priority !== activePriority) {
      return false;
    }

    // Scope filter (Work vs Personal)
    if (activeScope === 'work' && !t.is_work) return false;
    if (activeScope === 'personal' && t.is_work) return false;

    return true;
  });

  return `
    <div class="task-list-view">
      <!-- Header -->
      <div class="dashboard-header">
        <div>
          <h1 class="greeting-text">📅 Agenda & Tarefas</h1>
          <p class="date-text">Gerencie seus projetos, prazos e prioridades com precisão.</p>
        </div>
        <div>
          <button class="btn-primary" id="btn-agenda-new-task">
            <span>＋</span> <span>Nova Tarefa</span>
          </button>
        </div>
      </div>

      <!-- Filters & Tabs Bar -->
      <div class="card-section" style="padding: 14px 20px; margin-bottom: 24px;">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px;">
          <!-- Tabs -->
          <div style="display: flex; gap: 6px; flex-wrap: wrap;">
            <button class="mode-btn ${activeTab === 'today' ? 'active' : ''}" data-tab="today">Hoje</button>
            <button class="mode-btn ${activeTab === 'upcoming' ? 'active' : ''}" data-tab="upcoming">Próximas</button>
            <button class="mode-btn ${activeTab === 'overdue' ? 'active' : ''}" data-tab="overdue">Atrasadas</button>
            <button class="mode-btn ${activeTab === 'completed' ? 'active' : ''}" data-tab="completed">Concluídas</button>
            <button class="mode-btn ${activeTab === 'all' ? 'active' : ''}" data-tab="all">Todas</button>
          </div>

          <!-- Secondary Filters (Priority & Scope) -->
          <div style="display: flex; gap: 10px; align-items: center; flex-wrap: wrap;">
            <!-- Scope Filter -->
            <select id="select-scope-filter" class="form-select" style="width: auto; padding: 6px 12px; font-size: 0.85rem;">
              <option value="all" ${activeScope === 'all' ? 'selected' : ''}>Todos os Escopos</option>
              <option value="work" ${activeScope === 'work' ? 'selected' : ''}>💼 Trabalho Autônomo</option>
              <option value="personal" ${activeScope === 'personal' ? 'selected' : ''}>🏠 Vida Pessoal</option>
            </select>

            <!-- Priority Filter -->
            <select id="select-priority-filter" class="form-select" style="width: auto; padding: 6px 12px; font-size: 0.85rem;">
              <option value="all" ${activePriority === 'all' ? 'selected' : ''}>Todas as Prioridades</option>
              <option value="urgent" ${activePriority === 'urgent' ? 'selected' : ''}>🔥 Urgente</option>
              <option value="high" ${activePriority === 'high' ? 'selected' : ''}>⚡ Alta</option>
              <option value="medium" ${activePriority === 'medium' ? 'selected' : ''}>🔹 Média</option>
              <option value="low" ${activePriority === 'low' ? 'selected' : ''}>🌱 Baixa</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Task Items List -->
      <div class="card-section">
        <div class="section-title">
          <span>Itens Encontrados (${filteredTasks.length})</span>
        </div>

        ${filteredTasks.length === 0 ? `
          <div style="text-align: center; padding: 48px 20px; color: var(--text-muted);">
            <div style="font-size: 2.2rem; margin-bottom: 12px;">🏖️</div>
            <h3>Nenhuma tarefa para os filtros selecionados</h3>
            <p style="margin-top: 6px;">Crie um novo item para começar a planejar seu tempo.</p>
            <button class="btn-primary" id="btn-empty-list-add" style="margin-top: 16px;">
              <span>＋</span> <span>Criar Tarefa</span>
            </button>
          </div>
        ` : `
          <div class="tasks-container">
            ${filteredTasks.map((t) => renderFullTaskItem(t)).join('')}
          </div>
        `}
      </div>
    </div>
  `;
}

function renderFullTaskItem(task) {
  const isDone = task.status === 'completed';
  const pMeta = getPriorityMeta(task.priority);
  const locMeta = getLocationMeta(task.context_location);
  const dateLabel = getRelativeDateLabel(task.due_date);
  const checklists = task.checklists || [];

  const completedChecklists = checklists.filter((c) => c.is_completed).length;

  return `
    <div class="task-item ${isDone ? 'completed' : ''}" style="flex-direction: column; align-items: stretch; gap: 8px;">
      <div style="display: flex; align-items: flex-start; gap: 14px;">
        <div class="task-checkbox ${isDone ? 'checked' : ''}" data-action="toggle-task" data-id="${task.id}">
          ${isDone ? '✓' : ''}
        </div>

        <div class="task-info">
          <div class="task-title" data-action="edit-task" data-id="${task.id}">
            ${escapeHtml(task.title)}
          </div>
          ${task.description ? `
            <div style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 6px;">
              ${escapeHtml(task.description)}
            </div>
          ` : ''}

          <div class="task-meta">
            <span class="badge ${pMeta.class}">${pMeta.icon} ${pMeta.label}</span>
            <span style="color: ${task.due_date && task.due_date < new Date().toISOString().split('T')[0] && !isDone ? 'var(--rose-accent)' : 'var(--text-secondary)'};">
              📅 ${dateLabel}
            </span>
            ${task.due_time ? `<span>⏰ ${formatTimeBR(task.due_time)}</span>` : ''}
            ${task.estimated_minutes > 0 ? `<span>⏱️ ${task.estimated_minutes} min</span>` : ''}
            <span>${locMeta.icon} ${locMeta.label}</span>
            <span>${task.is_work ? '💼 Negócio' : '🏠 Pessoal'}</span>
            ${task.category_name ? `<span style="color: ${task.category_color || 'var(--text-muted)'};">🏷️ ${escapeHtml(task.category_name)}</span>` : ''}
          </div>
        </div>

        <div style="display: flex; gap: 6px;">
          <button class="btn-icon" data-action="edit-task" data-id="${task.id}" title="Editar" style="width: 32px; height: 32px; font-size: 0.85rem;">
            ✏️
          </button>
          <button class="btn-icon" data-action="delete-task" data-id="${task.id}" title="Excluir" style="width: 32px; height: 32px; font-size: 0.85rem; color: var(--rose-accent);">
            🗑️
          </button>
        </div>
      </div>

      <!-- Checklist Section if task has checklists -->
      ${checklists.length > 0 ? `
        <div style="margin-left: 36px; padding-top: 6px; border-top: 1px dashed var(--border-subtle);">
          <div style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 6px;">
            Checklist (${completedChecklists}/${checklists.length})
          </div>
          <div style="display: flex; flex-direction: column; gap: 4px;">
            ${checklists.map((chk) => `
              <div style="display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: ${chk.is_completed ? 'var(--text-muted)' : 'var(--text-primary)'};">
                <span style="color: ${chk.is_completed ? 'var(--emerald-accent)' : 'var(--text-muted)'};">${chk.is_completed ? '☑' : '☐'}</span>
                <span style="${chk.is_completed ? 'text-decoration: line-through;' : ''}">${escapeHtml(chk.title)}</span>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}
    </div>
  `;
}

function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

export function attachTaskListEvents(container) {
  // Tabs
  container.querySelectorAll('[data-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      activeTab = btn.dataset.tab;
      window.dispatchEvent(new CustomEvent('infinity:rerender-view'));
    });
  });

  // Scope Filter
  const scopeSelect = container.querySelector('#select-scope-filter');
  if (scopeSelect) {
    scopeSelect.addEventListener('change', (e) => {
      activeScope = e.target.value;
      window.dispatchEvent(new CustomEvent('infinity:rerender-view'));
    });
  }

  // Priority Filter
  const prioritySelect = container.querySelector('#select-priority-filter');
  if (prioritySelect) {
    prioritySelect.addEventListener('change', (e) => {
      activePriority = e.target.value;
      window.dispatchEvent(new CustomEvent('infinity:rerender-view'));
    });
  }

  // New Task
  const newBtn = container.querySelector('#btn-agenda-new-task') || container.querySelector('#btn-empty-list-add');
  if (newBtn) {
    newBtn.addEventListener('click', () => store.openTaskModal());
  }

  // Task Toggle
  container.querySelectorAll('[data-action="toggle-task"]').forEach((el) => {
    el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.id;
      try {
        await api.toggleTask(id);
        window.dispatchEvent(new CustomEvent('infinity:refresh-data'));
      } catch (err) {
        showToast(err.message, 'error');
      }
    });
  });

  // Edit Task
  container.querySelectorAll('[data-action="edit-task"]').forEach((el) => {
    el.addEventListener('click', async () => {
      const id = el.dataset.id;
      try {
        const res = await api.getTask(id);
        if (res.success) {
          store.openTaskModal(res.data);
        }
      } catch (err) {
        showToast(err.message, 'error');
      }
    });
  });

  // Delete Task
  container.querySelectorAll('[data-action="delete-task"]').forEach((el) => {
    el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.id;
      if (confirm('Tem certeza de que deseja excluir esta tarefa?')) {
        try {
          await api.deleteTask(id);
          showToast('Tarefa excluída com sucesso.', 'info');
          window.dispatchEvent(new CustomEvent('infinity:refresh-data'));
        } catch (err) {
          showToast(err.message, 'error');
        }
      }
    });
  });
}
